import {
  TextField,
  Button,
  Box,
  Typography,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Divider,
  Tooltip,
  Grid,
  FormControlLabel,
  Checkbox,
  Avatar,
  InputAdornment,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VerifiedIcon from '@mui/icons-material/Verified';
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ColorPicker, { LIGHT_COLOR_LIST, DARK_COLOR_LIST } from './ColorPicker';
import { NA_LABEL } from '../common/AppConstants';

export const DynamicFieldLabel = ({
  field,
}) => {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
      <Typography variant="h5" gutterBottom>
        {field.label}
        {field.required && (
          <Typography component="span" color="error" ml={0.5}>
            *
          </Typography>
        )}
      </Typography>
      <Tooltip title={field.help_text} arrow>
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'pointer', mt: '-2px' }} />
      </Tooltip>
    </Box>
  );
};

export const DynamicFormField = ({
  field,
  isCustomField = false,
  formData,
  formErrors,
  handleChange,
  handleColorChange,
  relatedData,
  color_shade
}) => {
  if (field.extra) return null;
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',       // vertical alignment
        // justifyContent: 'center',   // horizontal alignment
        width: '100%',
        height: '100%',
      }}
    >
      <Box key={field.attr} width= '100%'>
        {/* Field Label */}
        {field.type !== "boolean" && (
          <DynamicFieldLabel field={field}/>
        )}

        {/* Data Input Field */}
        {field.type === 'choice' && ((!isCustomField && relatedData[field.attr]) || true) ? (
          <FormControl
            fullWidth
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
          >
            {/* <InputLabel id={field.attr}>{field.label}</InputLabel> */}
            <Select
              // labelId={field.attr}
              id={field.attr}
              value={isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]}
              // label={field.label}
              onChange={(e) => handleChange(field.attr, e.target.value, isCustomField)}
              fullWidth
              displayEmpty  // This allows showing placeholder when value is empty
            >
              {/* Add placeholder/clear option for nullable fields */}
              {!field.required && (
                <MenuItem value="">
                  <em style={{ color: '#666' }}>Select {field.label}</em>
                </MenuItem>
              )}
              
              {Object.entries(relatedData[field.attr] || {}).map(
                ([option_key, option_value]) => (
                  <MenuItem key={option_key} value={option_key}>
                    {option_value.label}
                  </MenuItem>
                ),
              )}
            </Select>
            {(isCustomField ? formErrors.custom_data?.[field.attr] : formErrors[field.attr]) && (
              <FormHelperText>
                {isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]}
              </FormHelperText>
            )}
          </FormControl>
        ) : field.type === 'boolean' ? (
          <FormControl
            fullWidth
            required={field.required || false}
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
            component="fieldset"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]}
                  onChange={(e) => handleChange(field.attr, e.target.checked, isCustomField)}
                  name={field.attr}
                />
              }
              label={<DynamicFieldLabel field={field}/>}
            />
            <FormHelperText>
              {(isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]) || ''}
            </FormHelperText>
          </FormControl>
        ) : field.attr === 'color' ? (
          <ColorPicker
            handleColorChange={handleColorChange}
            color_shade={color_shade}
            defaultColor={formData[field.attr]}
          />
        ) : field.type === 'decimal' ? (
          <TextField
            fullWidth
            // key={field.attr} // key defined on outer box
            // label={field.label}
            name={field.attr}
            type="number"
            inputProps={{ step: "1.0" }} // adjust precision as needed - "0.01"
            value={(isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]) || ''}
            onChange={(e) => handleChange(field.attr, e.target.value, isCustomField)}
            required={field.required || false}
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
            helperText={(isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]) || ''}
          />
        ) : field.type === 'phone' ? (
          <TextField
            fullWidth
            // key={field.attr} // key defined on outer box
            // label={field.label}
            name={field.attr}
            type="tel"
            value={(isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]) || ''}
            onChange={(e) => handleChange(field.attr, e.target.value, isCustomField)}
            required={field.required || false}
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
            helperText={(isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]) || ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img
                    // src="https://flagcdn.com/48x36/in.png"
                    src="https://flagcdn.com/w40/in.png"
                    alt="IN"
                    style={{ width: 20, height: 15, marginRight: 6, objectFit: 'contain', display: 'inline-block', verticalAlign: 'middle', }}
                  />
                  (+91)
                </InputAdornment>
              ),
            }}
            inputProps={{
              pattern: "[6-9][0-9]{9}",
              placeholder: "9876543210",
              maxLength: 10,
              inputMode: "numeric", // Better mobile keyboard
            }}
          />
        ) : field.type === 'url' ? (
          <TextField
            fullWidth
            // key={field.attr} // key defined on outer box
            // label={field.label}
            name={field.attr}
            type="url"
            value={(isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]) || ''}
            onChange={(e) => handleChange(field.attr, e.target.value, isCustomField)}
            required={field.required || false}
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
            helperText={(isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]) || ''}
          />
        ) : field.type === 'datetime' ? (
          <TextField
            fullWidth
            // key={field.attr} // key defined on outer box
            // label={field.label}
            name={field.attr}
            type="datetime-local"
            value={(isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]) || ''}
            onChange={(e) => handleChange(field.attr, e.target.value, isCustomField)}
            required={field.required || false}
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
            helperText={(isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]) || ''}
            InputLabelProps={{
              shrink: true,
            }}
          />
        ) : ( // Default to text field
          <TextField
            fullWidth
            // key={field.attr} // key defined on outer box
            // label={field.label}
            name={field.attr}
            type='text'  // default type = text
            value={(isCustomField ? formData.custom_data?.[field.attr] : formData[field.attr]) || ''}
            onChange={(e) => handleChange(field.attr, e.target.value, isCustomField)}
            required={field.required || false}
            disabled={field.editable === false}
            error={isCustomField ? Boolean(formErrors.custom_data?.[field.attr]) : Boolean(formErrors[field.attr])}
            helperText={(isCustomField ? formErrors.custom_data?.[field.attr]?.[0] : formErrors[field.attr]?.[0]) || ''}
          />
        )}
      </Box>
    </Box>
  );
};

export const CustomFieldInline = ({
  field,
  dataDict,
}) => {
  const isCustomField=true; // ToDo: When supporting non custom fields as well
  const relatedData={}; // ToDo: When supporting non custom fields as well
  
  return (
    <Box key={field.attr}
      width= '100%'
      sx={{
        display: 'flex',
        alignItems: 'center',       // vertical alignment
        gap: 0.5,
      }}
    >
      {/* Field Label */}
      {field.type !== 'boolean' && (
        <Typography component="span" variant="body2" fontWeight="bold">
          {field.label}:
        </Typography>
      )}
      
      {/* Field Data */}
      {field.type === 'boolean' ? (
        <FormControlLabel
          control={
            <Checkbox
              disabled={true}
              checked={isCustomField ? dataDict.custom_data?.[field.attr] : dataDict[field.attr]}
              name={field.attr}
              sx={{
                py: 0,
                px: 1,
              }}
            />
          }
          label={
            <Typography component="span" variant="body2" fontWeight="bold">
              {field.label}
            </Typography>
          }
        />
      ) : ( // Default to text field
        <Typography variant="body2">
          {dataDict.custom_data?.[field.attr] ?? NA_LABEL}
        </Typography>
      )}
    </Box>
  );
};

export const ProfileAvatar = ({
  dataDict,
  handleSendInviteModalOpen,
}) => {
  const isClickable = Boolean(handleSendInviteModalOpen);

  const avatarContent = (
    <>
      <Avatar
        src={dataDict?.profile_picture}
        alt={dataDict?.first_name || ''}
        sx={{
          width: 32,
          height: 32,
          borderRadius: 2, // 👈 Rounded square (theme spacing, e.g., 2 = 16px)
          boxShadow: '0 0 2px 2px rgba(255, 255, 255, 0.5)', // halo effect
        }}
        imgProps={{
          referrerPolicy: 'no-referrer',
          onError: () => console.warn('Avatar image failed to load'),
        }}
      >
        {dataDict?.first_name?.[0]?.toUpperCase() || ''}
      </Avatar>

      {isClickable && (
        <VerifiedIcon
          fontSize="small"
          color={dataDict?.verified ? 'info' : 'inherit'}
          sx={{
            width: 16,
            height: 16,
            position: 'absolute',
            bottom: -4,
            right: -4,
            backgroundColor: 'white',
            borderRadius: '50%',
            boxShadow: 1,
          }}
        />
      )}
    </>
  );

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      {isClickable ? (
        <Tooltip title={!dataDict['verified'] ? "Unverified user, Send invitation to join" : "Verified user"} arrow>
          <IconButton onClick={handleSendInviteModalOpen} size="small" sx={{ p: 0 }}>
            {avatarContent}
          </IconButton>
        </Tooltip>
      ) : (
        avatarContent
      )}
    </Box>
  );
};

export const NumberInput = ({
  value,
  onChange,
  min = 0,
  step = 1,
  labelSuffix,
  helperText,
  size = "small",
  fullWidth = true,
}) => {
  const sanitize = (val) => (Number.isNaN(val) ? min : Math.max(min, val));

  const handleInputChange = (e) => {
    onChange(sanitize(parseInt(e.target.value, 10)));
  };

  const increment = () => onChange(sanitize((value ?? 0) + step));
  const decrement = () => onChange(sanitize((value ?? 0) - step));

  return (
    <TextField
      type="text"
      size={size}
      fullWidth={fullWidth}
      value={value}
      onChange={handleInputChange}
      helperText={helperText}
      slotProps={{
        htmlInput: {
          min,
          step,
          style: { textAlign: "center" }, // centers value between the two buttons
        },
        input: {
          sx: { px: 0 }, // removes adornment-side padding that offsets buttons
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <IconButton
                size="small"
                onClick={decrement}
                disabled={value <= min}
                sx={{ borderRadius: 0, height: "100%", px: 1 }} // full-height hit area
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 0, gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={increment}
                sx={{ borderRadius: 0, height: "100%", px: 1 }} // full-height hit area
              >
                <AddIcon fontSize="small" />
              </IconButton>
              {labelSuffix && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: { xs: "none", md: "block" }, // hide < md
                    pr: 1,  // moved padding here, off the fragment
                  }}
                >
                  {labelSuffix}
                </Typography>
              )}
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
